import { cabecalhosDoApp } from "@/lib/cabecalhos-do-app"
import authService from "@/lib/services/auth-service"
import { uploadStore } from "./upload-store"

const BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.welltrainer.com.br"

/** Upload cancelado pelo usuário (ou abortado). Call sites devem IGNORAR este
 *  erro, não é falha, não deve gerar toast nem report. */
export class UploadAbortError extends Error {
  constructor() {
    super("Upload cancelado")
    this.name = "UploadAbortError"
  }
}

/** Erro HTTP (status != 2xx) ou de rede durante o upload. */
export class UploadError extends Error {
  status: number
  data: any
  constructor(status: number, message: string, data: any) {
    super(message)
    this.name = "UploadError"
    this.status = status
    this.data = data
  }
}

export interface UploadOptions {
  /** rota relativa à API, ex.: "/exercicio/upload-video" */
  path: string
  formData: FormData
  /** rótulo exibido no card de progresso (ex.: nome do arquivo) */
  label: string
  method?: "POST" | "PATCH" | "PUT"
  /** não exibe card de progresso global (uploads pequenos/instantâneos) */
  silent?: boolean
  /**
   * Recebe a porcentagem enviada, de 0 a 100.
   *
   * Vale mesmo com `silent`: é assim que uma tela que já desenha a própria
   * barra (o popup de vídeo do exercício) dispensa o card global sem perder a
   * informação de quanto falta.
   */
  aoProgredir?: (pct: number) => void
  /**
   * Cancela o envio por fora.
   *
   * Para a tela que desenha a própria linha do arquivo, com o "Cancelar" dela:
   * com `silent` não existe card global, e sem isto não havia como parar o envio.
   */
  sinal?: AbortSignal
}

function gerarId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `upl_${crypto.randomUUID()}`
  }
  return `upl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Faz upload de FormData via XMLHttpRequest (necessário p/ progresso de upload
 * `fetch` não expõe `upload.onprogress`). Registra o upload no store global pra
 * mostrar barra de progresso + botão de cancelar, faz retry de 401 com refresh
 * de token e resolve com o JSON da resposta.
 *
 * Lança `UploadAbortError` quando o usuário cancela (ignorar no call site) e
 * `UploadError` em falha de HTTP/rede.
 */
export async function uploadFile<T = any>(opts: UploadOptions): Promise<T> {
  const { path, formData, label, method = "POST", silent = false, aoProgredir, sinal } = opts
  if (sinal?.aborted) throw new UploadAbortError()
  const id = gerarId()

  let cancelado = false
  let xhrAtual: XMLHttpRequest | null = null
  const cancel = () => {
    cancelado = true
    xhrAtual?.abort()
  }
  sinal?.addEventListener("abort", cancel, { once: true })

  if (!silent) {
    uploadStore.add({
      id,
      label,
      progress: 0,
      status: "uploading",
      indeterminate: true,
      cancel,
    })
  }

  const tentar = (token: string | null) =>
    new Promise<{ status: number; text: string }>((resolve, reject) => {
      /* Cancelado entre uma tentativa e outra (durante a renovação do token, por
         exemplo): não abre um envio novo que ninguém quer mais. */
      if (cancelado) {
        reject(new UploadAbortError())
        return
      }
      const xhr = new XMLHttpRequest()
      xhrAtual = xhr
      xhr.open(method, `${BASE_URL}${path}`)
      if (token) xhr.setRequestHeader("Authorization", token)
      /* Os mesmos cabeçalhos que o `apiFetch` manda, a plataforma entre eles: sem ela o
         log da API marcava todo envio de arquivo feito dentro do app como `mobile_web`.
         Cada um entra UMA vez: o XMLHttpRequest junta o mesmo nome posto de novo
         ("app_ios, app_ios"), e a API não reconhece o valor junto. */
      for (const [chave, valor] of Object.entries(cabecalhosDoApp())) xhr.setRequestHeader(chave, valor)
      // NÃO setar Content-Type: o browser injeta o boundary do multipart.

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return
        const pct = Math.min(100, Math.round((e.loaded / e.total) * 100))
        aoProgredir?.(pct)
        if (!silent) uploadStore.update(id, { progress: pct, indeterminate: false })
      }
      xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText })
      xhr.onerror = () => reject(new UploadError(0, "Falha de conexão durante o envio.", null))
      xhr.onabort = () => reject(new UploadAbortError())
      xhr.send(formData)
    })

  try {
    let token = await authService.getValidToken()
    let { status, text } = await tentar(token)

    // Retry de 401 com refresh (mesmo fluxo do apiFetch).
    if (status === 401 && typeof window !== "undefined" && !cancelado) {
      let novo: string | null = null
      try {
        novo = await authService.forceRefreshToken()
      } catch {}
      if (novo && !cancelado) {
        token = novo
        ;({ status, text } = await tentar(token))
      }
      if (status === 401 && !novo) authService.handleSessionLost()
    }

    let body: any = {}
    try {
      body = JSON.parse(text)
    } catch {}

    if (status < 200 || status >= 300) {
      throw new UploadError(
        status,
        body.mensagem ||
          body.message ||
          (status === 401 ? "Sessão expirada. Tente novamente." : "Erro ao enviar o arquivo."),
        body,
      )
    }

    if (!silent) {
      uploadStore.update(id, {
        progress: 100,
        status: "success",
        indeterminate: false,
      })
      setTimeout(() => uploadStore.remove(id), 1500)
    }
    return body as T
  } catch (err: any) {
    // Cancelamento intencional: some sem alarde, não propaga como falha visível.
    if (cancelado || err instanceof UploadAbortError) {
      if (!silent) uploadStore.remove(id)
      throw new UploadAbortError()
    }
    if (!silent) {
      uploadStore.update(id, {
        status: "error",
        error: err?.message || "Erro ao enviar o arquivo.",
      })
      setTimeout(() => uploadStore.remove(id), 6000)
    }
    throw err
  }
}
